
import { Fragment, createContext } from "react";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";

// Context for dropdown state (for compound components)
const DropdownContext = createContext<{ close: () => void } | null>(null);

// Root Dropdown component
interface DropdownProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Dropdown({ children }: DropdownProps) {
  // Note: Headless UI Menu is uncontrolled - it manages open/closed state internally
  // The open/onOpenChange props are accepted for API compatibility but not used
  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ close }) => (
        <DropdownContext.Provider value={{ close }}>
          {children}
        </DropdownContext.Provider>
      )}
    </Menu>
  );
}

// Dropdown Trigger
interface DropdownTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

export function DropdownTrigger({ children, asChild }: DropdownTriggerProps) {
  if (asChild) {
    return <MenuButton as={Fragment}>{children}</MenuButton>;
  }
  return <MenuButton>{children}</MenuButton>;
}

// Dropdown Content
interface DropdownContentProps {
  children: React.ReactNode;
  align?: "start" | "end" | "left" | "right";
  className?: string;
}

export function DropdownContent({
  children,
  align = "right",
  className,
}: DropdownContentProps) {
  const alignClass =
    align === "start" || align === "left" ? "left-0" : "right-0";

  return (
    <Transition
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <MenuItems
        className={clsx(
          "absolute z-50 mt-2 w-56 origin-top-right rounded-lg bg-surface border border-neutral-subtle shadow-lg focus:outline-none",
          alignClass,
          className
        )}
      >
        <div className="py-1">{children}</div>
      </MenuItems>
    </Transition>
  );
}

// Dropdown Item
interface DropdownItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  className?: string;
  asChild?: boolean;
}

export function DropdownItem({
  children,
  onClick,
  disabled,
  danger,
  className,
  asChild,
}: DropdownItemProps) {
  return (
    <MenuItem disabled={disabled}>
      {({ focus }) => {
        const baseClasses = clsx(
          "flex w-full items-center gap-2 px-4 py-2 text-sm",
          disabled && "opacity-50 cursor-not-allowed",
          danger
            ? focus
              ? "bg-danger-subtle text-danger"
              : "text-danger"
            : focus
            ? "bg-surface-raised text-text"
            : "text-text-quiet",
          className
        );

        if (asChild) {
          // When asChild is true, we expect children to be a single element
          // and we apply the styles to it
          return (
            <div className={baseClasses} onClick={onClick}>
              {children}
            </div>
          );
        }

        return (
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={baseClasses}
          >
            {children}
          </button>
        );
      }}
    </MenuItem>
  );
}

// Dropdown Divider
export function DropdownDivider() {
  return <div className="my-1 border-t border-neutral-subtle" />;
}

// Legacy API support (items array)
export interface LegacyDropdownItem {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  danger?: boolean;
}

export interface LegacyDropdownProps {
  trigger: React.ReactNode;
  items: LegacyDropdownItem[];
  align?: "left" | "right";
  className?: string;
}

export function LegacyDropdown({
  trigger,
  items,
  align = "right",
  className,
}: LegacyDropdownProps) {
  return (
    <Menu as="div" className={clsx("relative inline-block text-left", className)}>
      <MenuButton as={Fragment}>{trigger}</MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          className={clsx(
            "absolute z-50 mt-2 w-56 origin-top-right rounded-lg bg-surface border border-neutral-subtle shadow-lg focus:outline-none",
            align === "right" ? "right-0" : "left-0"
          )}
        >
          <div className="py-1">
            {items.map((item, index) => (
              <MenuItem key={index} disabled={item.disabled}>
                {({ focus }) => {
                  const baseClasses = clsx(
                    "flex w-full items-center gap-2 px-4 py-2 text-sm",
                    item.disabled && "opacity-50 cursor-not-allowed",
                    item.danger
                      ? focus
                        ? "bg-danger-subtle text-danger"
                        : "text-danger"
                      : focus
                      ? "bg-surface-raised text-text"
                      : "text-text-quiet"
                  );

                  if (item.href && !item.disabled) {
                    return (
                      <a href={item.href} className={baseClasses}>
                        {item.icon}
                        {item.label}
                      </a>
                    );
                  }

                  return (
                    <button
                      type="button"
                      onClick={item.onClick}
                      disabled={item.disabled}
                      className={baseClasses}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  );
                }}
              </MenuItem>
            ))}
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
}

export default Dropdown;
